import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import ContactForm from "../../components/ContactForm";
import { Phone, Mail, Clock, Crown, ChevronLeft, ChevronRight, MessageSquare, Globe } from "lucide-react";
import villa3D from "../../images/luxurious_3d_villa.png";
import "./Contact.css";

const officeImages = [
  {
    id: 1,
    src: "/images/hr_sir (1).jpg",
    alt: "Nashik Head Office - Exterior View",
    title: "Head Office - Main Building",
    office: "Nashik (Head Office)",
  },
  {
    id: 2,
    src: "/images/IMG_8469.jpg",
    alt: "Nashik Head Office - Interior",
    title: "Head Office - Work Area",
    office: "Nashik (Head Office)",
  },
  {
    id: 3,
    src: "/images/3.jpg",
    alt: "Pune Branch Office - Exterior View",
    title: "Branch Office - Aston Plaza",
    office: "Nashik (Head Office)",
  },
];

export default function Contact() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const autoSlideRef = useRef(null);

  const stopAutoSlide = useCallback(() => {
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
    }
  }, []);

  const startAutoSlide = useCallback(() => {
    stopAutoSlide();
    autoSlideRef.current = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % officeImages.length);
    }, 4500);
  }, [stopAutoSlide]);

  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide();
  }, [startAutoSlide, stopAutoSlide]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % officeImages.length);
    startAutoSlide();
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? officeImages.length - 1 : prev - 1
    );
    startAutoSlide();
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
    startAutoSlide();
  };


  // Framer Motion Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const sectionVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <>
      <div className="contact_container py-5">
        {/* Floating Backdrop Decorative Blobs */}
        <div className="contact-bg-decorations">
          <div className="contact-decor-blob contact-decor-blob-1"></div>
          <div className="contact-decor-blob contact-decor-blob-2"></div>
          <div className="contact-decor-blob contact-decor-blob-3"></div>
        </div>

        <div className="container position-relative" style={{ zIndex: 2 }}>
          {/* Header Section */}
          <motion.div
            className="text-center mb-5 mt-5"
            variants={headerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="contact-badge mb-3">
              Say Hello
            </div>
            <h1 className="contact-title display-4 fw-bold mb-3">Get In Touch</h1>
            <p className="contact-subtitle lead text-muted">
              We'd love to hear from you! Reach out to us through any of our offices or contact methods below.
            </p>
          </motion.div>

          {/* Office Images Slider Section */}
          <motion.div
            className="row mb-5"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="col-12">
              <div className="office-gallery-card">
                <div className="text-center mb-4">
                  <h2 className="office-gallery-title mb-2">Our Office Network</h2>
                  <p className="text-muted">Strategically located to serve you better</p>
                </div>

                <div className="slider-container">
                  <div
                    className="slider-wrapper"
                    style={{
                      transform: `translateX(-${currentImageIndex * 100}%)`,
                      transition: "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
                    }}
                  >
                    {officeImages.map((image) => (
                      <div key={image.id} className="slider-slide">
                        <div className="image-wrapper">
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="office-image"
                          />
                          <div className="image-overlay">
                            <div className="overlay-content">
                              <h4>
                                {image.office.includes("Head Office") && (
                                  <Crown className="text-warning me-2" size={24} />
                                )}
                                {image.title}
                              </h4>
                              <p className="mb-0">{image.office}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  <motion.button
                    className="slider-btn slider-btn-left"
                    onClick={prevImage}
                    aria-label="Previous image"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ChevronLeft size={24} />
                  </motion.button>

                  <motion.button
                    className="slider-btn slider-btn-right"
                    onClick={nextImage}
                    aria-label="Next image"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ChevronRight size={24} />
                  </motion.button>
                </div>

                {/* Dots Indicator */}
                <div className="dots-container">
                  {officeImages.map((_, index) => (
                    <button
                      key={index}
                      className={`dot ${index === currentImageIndex ? "active" : ""}`}
                      onClick={() => goToImage(index)}
                      aria-label={`Go to image ${index + 1}`}
                    ></button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Info Cards */}
          <motion.div
            className="row mb-5 g-4 justify-content-center"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Nashik Head Office */}
            <div className="col-lg-4 col-md-6 parent">
              <motion.div
                className="card-3d-wrapper theme-light-blue"
                variants={cardVariants}
              >
                <div className="logo-concentric">
                  <span className="circle circle5">
                    <div className="live-animate-icon light-blue-crown">
                      <Crown size={22} className="crown-badge-3d" />
                    </div>
                  </span>
                </div>
                <div className="glass-panel"></div>
                <div className="card-3d-content">
                  <span className="title">Head Office</span>
                  <span className="subtitle d-flex align-items-center">
                    Nashik <span className="live-status-dot light-blue" title="Main Office"></span>
                  </span>
                  <span className="text">
                    Flat No. 1, Urvashi Apartment,<br />
                    Kusumagraj Smarak Path, Bhavik Nagar,<br />
                    Nashik, MH 422005
                  </span>
                </div>
                <div className="bottom-panel">
                  <div className="social-buttons-container">
                    <a href="tel:+919371001138" className="social-button" title="Call Office">
                      <Phone size={14} />
                    </a>
                    <a href="mailto:enquiry@theinnovativesolutions.in" className="social-button" title="Email Office">
                      <Mail size={14} />
                    </a>
                  </div>
                  <a
                    href="https://maps.google.com/?q=the+innovative+solutions+nashik"
                    target="_blank"
                    rel="noreferrer"
                    className="view-more"
                  >
                    <span className="view-more-button">View Map</span>
                    <svg className="svg-arrow" viewBox="0 0 24 24">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Contact details */}
            <div className="col-lg-4 col-md-6 parent">
              <motion.div
                className="card-3d-wrapper theme-accent"
                variants={cardVariants}
              >
                <div className="logo-concentric">
                  <span className="circle circle5">
                    <div className="live-animate-icon accent-mail">
                      <Mail size={22} />
                    </div>
                  </span>
                </div>
                <div className="glass-panel"></div>
                <div className="card-3d-content">
                  <span className="title">Contact Us</span>
                  <span className="subtitle d-flex align-items-center">
                    Phone & Email <span className="live-status-dot green" title="Support Active"></span>
                  </span>
                  <span className="text">
                    Phone: <a href="tel:+919371001138">+91 9371001138</a>
                    <br />
                    Email: <a href="mailto:enquiry@theinnovativesolutions.in">enquiry@theinnovative...</a>
                  </span>
                </div>
                <div className="bottom-panel">
                  <div className="social-buttons-container">
                    <a href="tel:+919371001138" className="social-button" title="Call Us">
                      <Phone size={14} />
                    </a>
                    <a href="https://wa.me/919371001138" target="_blank" rel="noreferrer" className="social-button" title="WhatsApp Us">
                      <MessageSquare size={14} />
                    </a>
                  </div>
                  <a
                    href="mailto:enquiry@theinnovativesolutions.in"
                    className="view-more"
                  >
                    <span className="view-more-button">Email Us</span>
                    <svg className="svg-arrow" viewBox="0 0 24 24">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Operating Hours */}
            <div className="col-lg-4 col-md-6 parent">
              <motion.div
                className="card-3d-wrapper theme-dark"
                variants={cardVariants}
              >
                <div className="logo-concentric">
                  <span className="circle circle5">
                    <div className="live-animate-icon dark-clock">
                      <Clock size={22} />
                    </div>
                  </span>
                </div>
                <div className="glass-panel"></div>
                <div className="card-3d-content">
                  <span className="title">Working Hours</span>
                  <span className="subtitle d-flex align-items-center">
                    Office Timings <span className="live-status-dot green" title="Operational"></span>
                  </span>
                  <span className="text">
                    Mon - Fri: 10:00 AM - 7:00 PM
                    <br />
                    Sat - Sun: Closed
                  </span>
                </div>
                <div className="bottom-panel">
                  <div className="social-buttons-container">
                    <span className="social-button" title="Clock Info">
                      <Clock size={14} />
                    </span>
                    <span className="social-button" title="Web Details">
                      <Globe size={14} />
                    </span>
                  </div>
                  <div className="view-more">
                    <span className="view-more-button">Active Now</span>
                    <svg className="svg-arrow" viewBox="0 0 24 24">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Contact Form Section Card */}
          <motion.div
            className="premium-form-card mb-5"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Background 3D Image & Dark Overlay Filter */}
            <div 
              className="premium-card-bg"
              style={{ backgroundImage: `url(${villa3D})` }}
            ></div>
            <div className="premium-card-overlay"></div>

            {/* Form Content Overlay */}
            <div className="row g-5 position-relative" style={{ zIndex: 1 }}>
              {/* Left Panel: Info */}
              <div className="col-lg-6">
                <div className="form-left-panel">
                  <div>
                    <h2 className="left-panel-title">You Have Questions,<br />We Have Answers</h2>
                    <p className="left-panel-subtitle">
                      Discover experiences you won't find anywhere else — thoughtfully designed to connect you with modern, custom IT solutions. Let us build your digital success story together.
                    </p>
                  </div>
                  
                  {/* Structured Details */}
                  <div className="contact-meta-grid" style={{ marginTop: '2.5rem' }}>
                    <div>
                      <h4 className="meta-item-title">Location</h4>
                      <p className="meta-item-text">
                        Nashik Head Office:<br />
                        Kusumagraj Smarak Path, Bhavik Nagar, Nashik, MH 422005
                      </p>
                    </div>
                    <div>
                      <h4 className="meta-item-title">Social Media</h4>
                      <p className="meta-item-text">
                        <a href="https://www.linkedin.com/in/the-innovative-solutions-18255222a/" target="_blank" rel="noreferrer">LinkedIn</a><br />
                        <a href="https://www.instagram.com/the.innovative.solutions/" target="_blank" rel="noreferrer">Instagram</a><br />
                        <a href="https://www.facebook.com/profile.php?id=100094476235504" target="_blank" rel="noreferrer">Facebook</a><br />
                        <a href="https://wa.me/919371001138?text=Hello%21%20Team%20Innovative%20Solutions" target="_blank" rel="noreferrer">WhatsApp</a>
                      </p>
                    </div>
                    <div>
                      <h4 className="meta-item-title">Email</h4>
                      <p className="meta-item-text">
                        <a href="mailto:enquiry@theinnovativesolutions.in">enquiry@theinnovative...</a>
                      </p>
                    </div>
                    <div>
                      <h4 className="meta-item-title">Contact</h4>
                      <p className="meta-item-text">
                        <a href="tel:+919371001138">+91 9371001138</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel: White Card Form */}
              <div className="col-lg-6">
                <div className="form-right-card">
                  <h3 className="right-card-title">Tell Us What You Need</h3>
                  <p className="right-card-subtitle">
                    Our team is ready to assist you with every detail, big or small.
                  </p>
                  <ContactForm />
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
}

